import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fingerprint, ScanFace, Shield, AlertCircle, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import useBiometricAuth from '@/hooks/useBiometricAuth';
import { cn } from '@/lib/utils';

/**
 * VerifyIdentityDialog - Modal for biometric verification before sensitive actions
 * 
 * Shows a subtle verification prompt that doesn't interrupt the flow too much.
 * Used when accessing sensitive information like emergency data, exporting PDFs, etc.
 */
const VerifyIdentityDialog = ({ 
  open, 
  onClose, 
  onSuccess,
  title = 'Verify Identity',
  description = 'Please verify your identity to continue',
  actionLabel = 'Continue'
}) => {
  const { isSupported, authenticate, isAuthenticating, error } = useBiometricAuth();
  const [verifying, setVerifying] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Auto-trigger verification when dialog opens
  useEffect(() => {
    if (open && isSupported) {
      handleVerify();
    }
  }, [open, isSupported]);

  const getBiometricIcon = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      return <ScanFace className="h-8 w-8 text-sky-500" />;
    }
    return <Fingerprint className="h-8 w-8 text-sky-500" />;
  };

  const getBiometricLabel = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) return 'Face ID / Passcode';
    return 'Fingerprint / PIN';
  };

  const handleVerify = async () => {
    setLocalError(null);
    setVerifying(true);

    try {
      const success = await authenticate();
      if (success) {
        onSuccess();
        onClose();
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setLocalError('Verification cancelled');
      } else {
        setLocalError(err.message || 'Verification failed');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-sm bg-white rounded-2xl shadow-xl overflow-hidden"
          data-testid="verify-identity-dialog"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-br from-sky-50 to-blue-50 px-6 py-8 text-center">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
            
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="w-20 h-20 mx-auto mb-4 rounded-full bg-white shadow-lg flex items-center justify-center"
            >
              {verifying || isAuthenticating ? (
                <Loader2 className="h-8 w-8 text-sky-500 animate-spin" />
              ) : (
                getBiometricIcon()
              )}
            </motion.div>
            
            <h2 className="text-xl font-semibold text-slate-800">{title}</h2>
            <p className="text-sm text-slate-500 mt-1">{description}</p>
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-4">
            {/* Error message */}
            {(localError || error) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-3 bg-red-50 text-red-600 rounded-xl text-sm"
              >
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{localError || error}</span>
              </motion.div>
            )}

            {/* Verify button */}
            <Button
              className="w-full h-12 bg-sky-500 hover:bg-sky-600 text-white rounded-xl shadow-lg shadow-sky-200/50"
              onClick={handleVerify}
              disabled={verifying || isAuthenticating}
            >
              {verifying || isAuthenticating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Verifying...
                </>
              ) : (
                <>
                  <Shield className="h-5 w-5 mr-2" />
                  Verify with {getBiometricLabel()}
                </>
              )}
            </Button>

            {/* Cancel button */}
            <Button
              variant="ghost"
              className="w-full text-slate-500 hover:text-slate-700"
              onClick={handleSkip}
            >
              Cancel
            </Button>
          </div>

          {/* Footer note */}
          <div className="px-6 pb-5">
            <p className="text-xs text-slate-400 text-center">
              This helps keep your sensitive information secure
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VerifyIdentityDialog;
